import type { CommunicationsGoalConfig } from "@/lib/hooks/api/automation/entity-automation-types";

export type GoalStatus = "red" | "yellow" | "green";
export type GoalMetricKey = "emails" | "calls" | "meetings" | "texts";
export type GoalMetricStatus = "red" | "yellow" | "green";

export interface GoalMetricProgress {
  key: GoalMetricKey;
  label: string;
  weeklyTarget: number;
  weekToDate: number;
  dailyCount: number;
  expectedByNow: number;
  remainingNeeded: number;
  remainingBusinessDays: number;
  requiredPerRemainingBusinessDay: number;
  isOnTrack: boolean;
  status: GoalMetricStatus;
}

export interface CommunicationsGoalEvaluation {
  status: GoalStatus;
  weekStartIso: string;
  dayStartIso: string;
  metrics: GoalMetricProgress[];
}

const YELLOW_LOWER_BOUND_RATIO = 0.9;

/** Default weekly SMS target when a goal config predates / omits `texts`. */
export const DEFAULT_TEXTS_WEEKLY_TARGET = 10;

function getMetricStatus(weekToDate: number, expectedByNow: number): GoalMetricStatus {
  if (expectedByNow <= 0) {
    return "green";
  }
  if (weekToDate > expectedByNow) {
    return "green";
  }
  const paceRatio = weekToDate / expectedByNow;
  if (paceRatio >= YELLOW_LOWER_BOUND_RATIO) {
    return "yellow";
  }
  return "red";
}

function getOverallGoalStatus(metricStatuses: GoalMetricStatus[]): GoalStatus {
  if (metricStatuses.length === 0) {
    return "green";
  }
  if (metricStatuses.every((status) => status === "green")) {
    return "green";
  }
  if (metricStatuses.some((status) => status === "red")) {
    return "red";
  }
  return "yellow";
}

function getStartOfUtcDay(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
}

function getStartOfUtcWeek(date: Date): Date {
  const dayStart = getStartOfUtcDay(date);
  const dayOfWeek = dayStart.getUTCDay(); // 0..6, Sunday=0
  const mondayOffset = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  const weekStart = new Date(dayStart);
  weekStart.setUTCDate(dayStart.getUTCDate() - mondayOffset);
  return weekStart;
}

function getEndOfUtcBusinessWeek(weekStart: Date): Date {
  const weekEnd = new Date(weekStart);
  weekEnd.setUTCDate(weekStart.getUTCDate() + 4); // Friday
  weekEnd.setUTCHours(23, 59, 59, 999);
  return weekEnd;
}

function countRemainingBusinessDaysAfterToday(now: Date, weekEnd: Date): number {
  const cursor = getStartOfUtcDay(now);
  cursor.setUTCDate(cursor.getUTCDate() + 1); // Start from tomorrow
  let count = 0;
  while (cursor <= weekEnd) {
    const day = cursor.getUTCDay();
    if (day >= 1 && day <= 5) {
      count += 1;
    }
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return count;
}

/**
 * The linked (secondary) `app_users.id`s from a goal config, merging the
 * canonical `linkedUserIds` array with the legacy single `linkedUserId` field
 * so rows saved before multi-account support still resolve. Deduped and
 * falsy-filtered. Does NOT exclude the primary — callers do that.
 */
export function resolveLinkedUserIds(goalConfig: {
  linkedUserIds?: string[] | null;
  linkedUserId?: string | null;
}): string[] {
  const ids: string[] = [];
  if (Array.isArray(goalConfig.linkedUserIds)) {
    ids.push(...goalConfig.linkedUserIds);
  }
  // Legacy single-account link (pre-multi-account rows).
  if (typeof goalConfig.linkedUserId === "string") {
    ids.push(goalConfig.linkedUserId);
  }
  return [...new Set(ids.filter((id) => typeof id === "string" && id.length > 0))];
}

/**
 * The set of `app_users.id`s whose communications count toward this goal —
 * the primary user plus every linked account. Deduped, falsy-filtered, and no
 * linked id can ever be the primary itself.
 */
export function resolveGoalUserIds(
  userId: string,
  goalConfig: CommunicationsGoalConfig
): string[] {
  const linked = resolveLinkedUserIds(goalConfig).filter((id) => id !== userId);
  return [...new Set([userId, ...linked])];
}

/**
 * Metrics that measure proactive OUTREACH — only outbound comms count.
 * (Emails and meetings a rep initiates.)
 */
const OUTREACH_ONLY_METRICS: ReadonlySet<GoalMetricKey> = new Set<GoalMetricKey>([
  "emails",
  "meetings",
]);

/**
 * Whether a communication of `direction` counts toward `metric`.
 *
 * - Emails & meetings are outreach → ONLY `outbound` counts.
 * - Calls & texts count in BOTH directions, on purpose. An inbound call or
 *   text a rep claims ("Assign to me" in Slack) is incoming work the rep is
 *   expected to pivot into a sales opportunity — it counts as much as an
 *   outbound touch. The cron must NOT decide that incoming work is "not selling";
 *   that pivot is the salesperson's job. See the COMMUNICATIONS_GOAL_INCOMING_WORK
 *   rule.
 */
export function metricCountsDirection(
  metric: GoalMetricKey,
  direction: string | null | undefined
): boolean {
  if (OUTREACH_ONLY_METRICS.has(metric)) {
    return direction === "outbound";
  }
  return true;
}

export function classifyMetric(typeLabel: string | null): GoalMetricKey | null {
  if (!typeLabel) {
    return null;
  }
  const normalized = typeLabel.toLowerCase();
  if (normalized.includes("email")) {
    return "emails";
  }
  if (normalized.includes("meeting") || normalized.includes("video")) {
    return "meetings";
  }
  if (normalized.includes("call") || normalized.includes("phone")) {
    return "calls";
  }
  if (normalized.includes("text") || normalized.includes("sms")) {
    return "texts";
  }
  return null;
}

export async function evaluateCommunicationsGoalForUser(
  supabase: any,
  userId: string,
  goalConfig: CommunicationsGoalConfig,
  now: Date = new Date()
): Promise<CommunicationsGoalEvaluation> {
  const dayStart = getStartOfUtcDay(now);
  const weekStart = getStartOfUtcWeek(now);
  const weekEnd = getEndOfUtcBusinessWeek(weekStart);
  const dayEnd = new Date(dayStart);
  dayEnd.setUTCDate(dayEnd.getUTCDate() + 1);

  // Count BOTH the primary account and any linked second account, summed.
  const goalUserIds = resolveGoalUserIds(userId, goalConfig);

  // No direction filter in the query — calls/texts count in BOTH directions
  // (incoming work counts), while emails/meetings are filtered to outbound in
  // JS via `metricCountsDirection`. Fetching both directions keeps that policy
  // in ONE place instead of a query that silently drops claimed inbound calls.
  const { data, error } = await supabase
    .from("communications")
    .select("type_label, occurred_at, direction")
    .in("user_id", goalUserIds)
    .gte("occurred_at", weekStart.toISOString())
    .lt("occurred_at", dayEnd.toISOString());

  if (error) {
    throw error;
  }

  const weekToDate = { emails: 0, calls: 0, meetings: 0, texts: 0 };
  const dailyCounts = { emails: 0, calls: 0, meetings: 0, texts: 0 };

  for (const row of data ?? []) {
    const metric = classifyMetric(row.type_label);
    if (!metric) {
      continue;
    }
    if (!metricCountsDirection(metric, row.direction)) {
      continue;
    }
    weekToDate[metric] += 1;
    const occurredAt = row.occurred_at ? new Date(row.occurred_at) : null;
    if (occurredAt && occurredAt >= dayStart && occurredAt < dayEnd) {
      dailyCounts[metric] += 1;
    }
  }

  const daysElapsed = Math.min(7, Math.floor((dayStart.getTime() - weekStart.getTime()) / 86400000) + 1);
  const ratio = daysElapsed / 7;
  const remainingBusinessDays = countRemainingBusinessDaysAfterToday(now, weekEnd);

  const computeRequiredPerDay = (remainingNeeded: number): number => {
    if (remainingNeeded <= 0) {
      return 0;
    }
    if (remainingBusinessDays <= 0) {
      return remainingNeeded;
    }
    return Number((remainingNeeded / remainingBusinessDays).toFixed(2));
  };

  const buildMetric = (
    key: GoalMetricKey,
    label: string,
    weeklyTarget: number,
    weekToDateValue: number,
    dailyCountValue: number
  ): GoalMetricProgress => {
    const expectedByNow = Math.ceil(weeklyTarget * ratio);
    const remainingNeeded = Math.max(0, weeklyTarget - weekToDateValue);
    const status = getMetricStatus(weekToDateValue, expectedByNow);
    return {
      key,
      label,
      weeklyTarget,
      weekToDate: weekToDateValue,
      dailyCount: dailyCountValue,
      expectedByNow,
      remainingNeeded,
      remainingBusinessDays,
      requiredPerRemainingBusinessDay: computeRequiredPerDay(remainingNeeded),
      isOnTrack: status !== "red",
      status,
    };
  };

  const metrics: GoalMetricProgress[] = [
    buildMetric("emails", "Emails", goalConfig.weeklyTargets.emails, weekToDate.emails, dailyCounts.emails),
    buildMetric("calls", "Calls", goalConfig.weeklyTargets.calls, weekToDate.calls, dailyCounts.calls),
    buildMetric(
      "meetings",
      "Meetings",
      goalConfig.weeklyTargets.meetings,
      weekToDate.meetings,
      dailyCounts.meetings
    ),
    buildMetric(
      "texts",
      "Texts",
      goalConfig.weeklyTargets.texts ?? DEFAULT_TEXTS_WEEKLY_TARGET,
      weekToDate.texts,
      dailyCounts.texts
    ),
  ];

  const overallStatus = getOverallGoalStatus(metrics.map((metric) => metric.status));

  return {
    status: overallStatus,
    weekStartIso: weekStart.toISOString(),
    dayStartIso: dayStart.toISOString(),
    metrics,
  };
}

